/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Tooltip } from 'antd';
import AvatarGroup from 'components/common/AvatarGroup';
import IconComponent from 'components/common/IconComponent';
import { DEFAULT_FORMAT_DATE_US } from 'constants/days';
import deviceSizes from 'constants/deviceSizes';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import useWindowSize from 'hooks/useWindowSize';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { IChatRoom } from 'types/messages';
import { getDefaultAvatarUrl } from 'utils';

export const checkUserIsInChatRoom = (userId: string, chatRoom?: IChatRoom) => {
  if (!chatRoom) return undefined;
  return chatRoom.chatRelations?.some((cR) => cR.user?.id === userId && !cR.leftTime);
};

export const useTransactionMessageTabProps = (props: Partial<{ chatRooms: IChatRoom[] }> = {}) => {
  const { chatRooms } = props;
  const userId = useCurrentLoggedInUser().id;
  const [selectedChatRoom, setSelectedChatRoom] = useState<IChatRoom>();
  const selectedAppointment = selectedChatRoom?.appointment;
  const [appointmentInfoModalVisible, setAppointmentInfoModalVisible] = useState<boolean>();

  const isUserInSelectedChatRoom = useMemo(() => checkUserIsInChatRoom(userId, selectedChatRoom), [
    userId,
    selectedChatRoom,
  ]);

  useEffect(() => {
    if (!chatRooms) return;
    if (chatRooms?.length === 1) {
      setSelectedChatRoom(chatRooms[0]);
    } else if (chatRooms?.length === 0) {
      setSelectedChatRoom(undefined);
    }
    if (selectedChatRoom) {
      const newSelectedChatRoom = chatRooms.find((cR) => cR.id === selectedChatRoom.id);
      if (newSelectedChatRoom) {
        setSelectedChatRoom(newSelectedChatRoom);
      }
    }
  }, [chatRooms]);
  const { isMobile } = useWindowSize(deviceSizes.numbers.md);

  const onAppointmentClick = (chatRoom: IChatRoom) => {
    setSelectedChatRoom(chatRoom);
  };
  const showAppointmentInfoModal = () => {
    setAppointmentInfoModalVisible(true);
  };
  const closeAppointmentInfoModal = () => {
    setAppointmentInfoModalVisible(false);
  };
  const selectedAppointmentDate = moment(selectedAppointment?.date).format(DEFAULT_FORMAT_DATE_US).toString();
  const userRelationsInChat = selectedChatRoom?.chatRelations?.filter((rel) => !rel.leftTime);
  const AppointmentChatWindowHeader = useMemo(
    () =>
      selectedChatRoom ? (
        <div className='w-full'>
          <div className='flex items-center justify-between'>
            {isMobile && (
              <div
                className='p-4'
                onClick={() => {
                  setSelectedChatRoom(undefined);
                }}>
                <IconComponent name='chevron-left' />
              </div>
            )}
            <div className='text-center p-5 flex-1 flex items-center justify-center'>
              <div>Appointment {selectedAppointmentDate}</div>
              {userRelationsInChat && userRelationsInChat.length > 0 && !isMobile && (
                <div className='ml-4'>
                  <Tooltip
                    title={
                      <div>
                        {userRelationsInChat?.map((r) => (
                          <div key={r.user?.id}>
                            {r.user?.firstName} {r.user?.lastName}
                          </div>
                        ))}
                      </div>
                    }
                    placement='topRight'>
                    <div>
                      <AvatarGroup
                        sources={userRelationsInChat.map(
                          (r) =>
                            r.user?.avatar?.thumbnailPipeFrom ||
                            getDefaultAvatarUrl(`${r.user?.firstName} ${r.user?.lastName}`),
                        )}
                      />
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
            {isMobile && (
              <div className='p-4' onClick={showAppointmentInfoModal}>
                <IconComponent name='exclamation' color='#038fde' />
              </div>
            )}
          </div>
          {!isUserInSelectedChatRoom && (
            <div className='pb-5 text-center text-lg text-secondary-color'>Not In Chat</div>
          )}
        </div>
      ) : undefined,
    [selectedChatRoom, isMobile],
  );

  return {
    selectedChatRoom,
    onAppointmentClick,
    setSelectedChatRoom,
    AppointmentChatWindowHeader,
    showAppointmentInfoModal,
    closeAppointmentInfoModal,
    appointmentInfoModalVisible,
    selectedAppointment,
    isUserInSelectedChatRoom,
  };
};
